export const BANNER_W = 2000;
export const BANNER_H = 260;

const loadImg = (src) => new Promise((res, rej) => {
  const i = new Image();
  i.crossOrigin = "anonymous";
  i.onload = () => res(i);
  i.onerror = rej;
  i.src = src;
});

const fitFont = (ctx, text, weight, size, family, maxW) => {
  let s = size + 4;
  do { s -= 4; ctx.font = `${weight} ${s}px ${family}`; } while (s > 24 && ctx.measureText(text).width > maxW);
  return [ctx.measureText(text).width, s];
};

export const BANNER_STYLES = {
  bold: { label: "Bold", name: ["800", '"Outfit", sans-serif'], tag: ["500", '"Plus Jakarta Sans", sans-serif'] },
  elegant: { label: "Elegant", name: ["700", '"Playfair Display", serif'], tag: ["italic 500", '"Playfair Display", serif'] },
  playful: { label: "Playful", name: ["700", '"Fredoka", sans-serif'], tag: ["500", '"Fredoka", sans-serif'] },
};

// Draws a 2000x260 banner: template background + optional shade, round logo, name and tagline.
export async function renderBanner({ bg, color = "#FFFFFF", align = "left", shade = true, name = "", tagline = "", logo, style = "bold", scale = 1, tagScale = 1 }) {
  const st = BANNER_STYLES[style] || BANNER_STYLES.bold;
  await Promise.all([document.fonts?.load(`${st.name[0]} 84px ${st.name[1]}`, name || "A"), document.fonts?.load(`${st.tag[0]} 40px ${st.tag[1]}`, tagline || "A")]).catch(() => {});
  const c = document.createElement("canvas");
  c.width = BANNER_W; c.height = BANNER_H;
  const ctx = c.getContext("2d");
  const img = await loadImg(bg);
  const s = Math.max(BANNER_W / img.width, BANNER_H / img.height);
  ctx.drawImage(img, (BANNER_W - img.width * s) / 2, (BANNER_H - img.height * s) / 2, img.width * s, img.height * s);
  if (shade) {
    const g = align === "center" ? ctx.createRadialGradient(BANNER_W / 2, BANNER_H / 2, 50, BANNER_W / 2, BANNER_H / 2, 900) : ctx.createLinearGradient(align === "left" ? 0 : BANNER_W, 0, align === "left" ? BANNER_W : 0, 0);
    g.addColorStop(0, "rgba(0,0,0,0.5)"); g.addColorStop(align === "center" ? 1 : 0.65, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, BANNER_W, BANNER_H);
  }
  const logoImg = logo ? await loadImg(logo).catch(() => null) : null;
  const L = logoImg ? 180 : 0, gap = logoImg ? 44 : 0;
  const maxText = 1300 - L;
  const [nameW, nameSize] = name ? fitFont(ctx, name, st.name[0], Math.round(84 * scale), st.name[1], maxText) : [0, 0];
  const nameFont = ctx.font;
  const [tagW, tagSize] = tagline ? fitFont(ctx, tagline, st.tag[0], Math.round(40 * tagScale), st.tag[1], maxText) : [0, 0];
  const tagFont = ctx.font;
  const top = (BANNER_H - (nameSize + 24 + tagSize)) / 2;
  const group = L + gap + Math.max(nameW, tagW);
  const x0 = align === "left" ? 100 : align === "right" ? BANNER_W - 100 - group : (BANNER_W - group) / 2;
  if (logoImg) {
    ctx.save();
    ctx.beginPath(); ctx.arc(x0 + L / 2, BANNER_H / 2, L / 2, 0, Math.PI * 2); ctx.closePath();
    ctx.fillStyle = "#fff"; ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = 18; ctx.fill();
    ctx.shadowBlur = 0; ctx.clip();
    const ls = Math.max((L - 16) / logoImg.width, (L - 16) / logoImg.height);
    ctx.beginPath(); ctx.arc(x0 + L / 2, BANNER_H / 2, L / 2 - 8, 0, Math.PI * 2); ctx.clip();
    ctx.drawImage(logoImg, x0 + L / 2 - (logoImg.width * ls) / 2, BANNER_H / 2 - (logoImg.height * ls) / 2, logoImg.width * ls, logoImg.height * ls);
    ctx.restore();
  }
  const tx = x0 + L + gap;
  ctx.fillStyle = color; ctx.textBaseline = "middle";
  const light = parseInt(color.slice(1, 3), 16) + parseInt(color.slice(3, 5), 16) + parseInt(color.slice(5, 7), 16) > 380;
  if (light) { ctx.shadowColor = "rgba(0,0,0,0.45)"; ctx.shadowBlur = 14; ctx.shadowOffsetY = 2; }
  if (name) { ctx.font = nameFont; ctx.fillText(name, tx, tagline ? top + nameSize / 2 : BANNER_H / 2); }
  if (tagline) { ctx.font = tagFont; ctx.globalAlpha = 0.92; ctx.fillText(tagline, tx, name ? top + nameSize + 24 + tagSize / 2 : BANNER_H / 2); ctx.globalAlpha = 1; }
  return c;
}

export const canvasToBlob = (c) => new Promise((res, rej) => {
  try { c.toBlob((b) => (b ? res(b) : rej(new Error("Could not create image"))), "image/jpeg", 0.9); } catch (e) { rej(e); }
});
